// components/info/InfoSidebarNav.tsx
'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';

export interface InfoSidebarNavItem {
  /** Anchor ID without "#" prefix (used as <Link href> when onSelect not provided). */
  id: string;
  /** Visible label text. */
  label: string;
  /** Optional lucide icon. */
  icon?: ReactNode;
}

interface InfoSidebarNavProps {
  items: InfoSidebarNavItem[];
  /** Currently-active item id (for visual highlight). */
  activeId?: string;
  /**
   * Optional click handler. If provided, items render as buttons that call
   * this; otherwise items render as anchor links to `#${id}`.
   */
  onSelect?: (id: string) => void;
  /** Optional heading shown above the nav list. */
  heading?: string;
  compact?: boolean;
}

/**
 * Sticky sidebar navigation used by:
 *   - Privacy/Terms pages: anchor scroll between in-page sections
 *   - FAQ page: category filter (provides onSelect callback)
 *
 * The dual-mode design (Link vs button) lets one component cover both
 * UX patterns without per-consumer divergence.
 */
export function InfoSidebarNav({ items, activeId, onSelect, heading, compact = false }: InfoSidebarNavProps) {
  return (
    <nav className={compact ? "lg:sticky lg:top-40 lg:w-56" : "lg:sticky lg:top-24 lg:w-64"} aria-label={heading ?? 'Section navigation'}>
      {heading && (
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
          {heading}
        </h3>
      )}
      <ul className="space-y-1">
        {items.map((item) => {
          const isActive = item.id === activeId;
          const className = isActive
            ? 'flex items-center gap-2 rounded-lg bg-wine-red px-3 py-2 text-sm text-white'
            : 'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-wine-red/5 hover:text-wine-red';
          const content = (
            <>
              {item.icon && <span className="h-4 w-4 flex-shrink-0">{item.icon}</span>}
              <span>{item.label}</span>
            </>
          );
          return (
            <li key={item.id}>
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(item.id)}
                  className={`${className} ${compact ? "min-h-11 w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#722f37]" : ""}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {content}
                </button>
              ) : (
                <Link
                  href={`#${item.id}`}
                  className={`${className} ${compact ? "min-h-11 w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#722f37]" : ""}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
